import { z } from "zod";
import { UuidSchema, PlayerNameSchema } from "./common.js";
import { SiteAdminPermissionKeySchema } from "./adminPermissions.js";
/**
 * Auth & session (SITE_SPEC §2).
 * Auth primaire contre `web_accounts` (PBKDF2 Portal), secondaire `etheria_auth`.
 */
/** Rôle dérivé du rang du joueur lié (etheria_player_ranks). */
export const ROLES = ["ADMIN", "MODO", "STAFF", "JOUEUR"];
export const RoleSchema = z.enum(ROLES);
export const LoginReqSchema = z.object({
    /** email (web_accounts) OU pseudo (secondaire etheria_auth). */
    identifier: z.string().min(1).max(128),
    password: z.string().min(1).max(256),
    remember: z.boolean().optional(),
});
export const RegisterReqSchema = z
    .object({
    email: z.string().email().max(190),
    password: z.string().min(8).max(256),
    passwordConfirm: z.string().min(8).max(256),
})
    .refine((v) => v.password === v.passwordConfirm, {
    message: "Les mots de passe ne correspondent pas",
    path: ["passwordConfirm"],
});
/** Identité de session résolue côté serveur (jamais fournie par le client). */
export const SessionUserSchema = z.object({
    accountId: z.number().int().nonnegative(),
    email: z.string().email().nullable(),
    /** uuid MC lié (null si compte non lié). */
    uuid: UuidSchema.nullable(),
    name: PlayerNameSchema.nullable(),
    role: RoleSchema,
    linked: z.boolean(),
    admin: z.boolean(),
    adminAll: z.boolean().default(false),
    adminPermissions: z.array(SiteAdminPermissionKeySchema).default([]),
    /**
     * Confidentialité : profil public masqué (web_accounts.hide_profile).
     * `.default(false)` (et non requis) pour rester compatible avec les JWT émis AVANT ce champ :
     * un ancien token sans `hideProfile` reste valide et est complété à false au décodage.
     */
    hideProfile: z.boolean().default(false),
});
/** Réponse de `GET /api/auth/me`. */
export const MeResSchema = z.object({
    authenticated: z.boolean(),
    user: SessionUserSchema.nullable(),
});
export const LoginResSchema = z.object({
    ok: z.literal(true),
    user: SessionUserSchema,
});
/**
 * Liaison compte via le code généré en jeu par `/lier` (table `etheria_web_link_codes`).
 * Le code est en base32 sans caractères ambigus (alphabet `A-Z` sans I/O + `2-9`), longueur
 * variable 6→8 selon la version du plugin (la colonne SQL est `CHAR(8)`). On normalise en
 * majuscules et on tolère espaces/tirets de mise en forme au collage ; la longueur est validée
 * ici (6 à 8) mais la vérification réelle se fait contre la ligne en base.
 */
export const LINK_CODE_MIN = 6;
export const LINK_CODE_MAX = 8;
export const LinkAccountReqSchema = z.object({
    code: z
        .string()
        .trim()
        .transform((s) => s.replace(/[\s-]/g, "").toUpperCase())
        .pipe(z.string().regex(/^[A-Z0-9]{6,8}$/, "Code invalide (6 à 8 caractères).")),
});
export const ChangePasswordReqSchema = z
    .object({
    currentPassword: z.string().min(1).max(256),
    newPassword: z.string().min(8).max(256),
    newPasswordConfirm: z.string().min(8).max(256),
})
    .refine((v) => v.newPassword === v.newPasswordConfirm, {
    message: "Les mots de passe ne correspondent pas",
    path: ["newPasswordConfirm"],
});
export const ResetPasswordReqSchema = z
    .object({
    code: z.string().length(6),
    newPassword: z.string().min(8).max(256),
    newPasswordConfirm: z.string().min(8).max(256),
})
    .refine((v) => v.newPassword === v.newPasswordConfirm, {
    message: "Les mots de passe ne correspondent pas",
    path: ["newPasswordConfirm"],
});
/** Confidentialité : masquer le profil public (hideProfile). */
export const PrivacyReqSchema = z.object({
    hideProfile: z.boolean(),
});
/** Changement d'adresse email du compte web (exige le mot de passe actuel). */
export const EmailChangeReqSchema = z.object({
    newEmail: z.string().email().max(190),
    currentPassword: z.string().min(1).max(256),
});
/**
 * Préférences de compte (Paramètres → notifications & rappels).
 * Persistées dans `web_account_prefs` (bootstrap paresseux, pattern ensure*Schema).
 * Tous les champs ont un défaut : un compte sans ligne de prefs reçoit ces valeurs.
 */
export const AccountPrefsSchema = z.object({
    /** Événements du Monde Vivant (Conscience) & donjons. */
    notifEvents: z.boolean().default(true),
    /** HDV : ventes conclues / objets à réclamer. */
    notifMarket: z.boolean().default(true),
    /** Guilde : invitations, banque, promotions. */
    notifGuild: z.boolean().default(true),
    /** Gazette d'Etheria (récapitulatif hebdomadaire). */
    newsletter: z.boolean().default(false),
});
//# sourceMappingURL=auth.js.map